import { Alert, AlertTitle, Box, CssBaseline, Typography, useTheme } from '@mui/material';
import { ThunkDispatch } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';

import { hasAnyRole, RoleType } from '@mse-reg/clients';
import { Spinner } from '../ui/controls/spinner';
import { ErrorBoundary } from '../ui/error/error-boundary';
import { Footer } from '../ui/structural/footer/footer';
import { Header } from '../ui/structural/header/header';
import { TextContent } from '../ui/structural/text-content';

import { DevRibbon } from './components/dev-ribbon';
import { Notifier } from './components/notifier';
import { appThunks } from './modules/application';
import AppDrawer from './modules/drawer';
import AppRoutes from './routes';
import { RootState } from './store';

export const App = (props: PropsFromRedux) => {
  const theme = useTheme();

  useEffect(() => {
    void props.loadInitialData();
  }, [props.loadInitialData]);

  const [open, setOpen] = useState(localStorage.getItem('drawerOpen') === 'true');

  const toggleDrawer = () => {
    localStorage.setItem('drawerOpen', open ? 'false' : 'true');
    setOpen(!open);
  };

  return (
    <Box className="app-root" sx={{ display: 'flex' }}>
      <CssBaseline />
      <Notifier />
      <DevRibbon profiles={props.profiles} />
      <Header hasAuthSession={props.isLoggedIn} drawerOpen={open} toggleDrawer={toggleDrawer} />
      <AppDrawer drawerOpen={open} toggleDrawer={toggleDrawer} />
      <Box
        component={'main'}
        sx={{
          backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
          paddingTop: '70px',
          flexGrow: 1,
          height: '100vh',
          backgroundSize: 'cover',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {props.applicationError ? (
            <TextContent>
              <Alert severity="error">
                <AlertTitle>An Error has occurred while loading the application!</AlertTitle>
                <Typography>{props.applicationError} </Typography>
                <br />
                <br />
                <Typography>
                  Please try to refresh by pressing F5. If this does not help, contact your administrator.
                </Typography>
              </Alert>
            </TextContent>
          ) : (
            <Spinner isLoading={props.loading}>
              {!props.loading && (
                <ErrorBoundary>
                  <AppRoutes />
                </ErrorBoundary>
              )}
            </Spinner>
          )}
        </Box>
        <Footer />
      </Box>
    </Box>
  );
};

const mapStateToProps = ({ auth, application, semesterSelection }: RootState) => ({
  isLoggedIn: auth.isLoggedIn,
  isAdmin: hasAnyRole(auth.account?.roles, [RoleType.ADMIN]),
  ribbonEnv: application.ribbonEnv,
  isInProduction: application.isProduction,
  isSwaggerEnabled: application.isSwaggerEnabled,
  profiles: application.profiles,
  applicationError: application.applicationError,
  selectedSemester: semesterSelection.selectedSemester,
  loading: application.loading,
});

const mapDispatchToProps = (dispatch: ThunkDispatch<RootState, any, any>) => {
  return {
    loadInitialData: async () => await dispatch(appThunks.loadInitialData()).unwrap(),
  };
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(App);
